/**
 * 动态权限系统
 * 完全基于后端返回的菜单数据，无需前端重复配置
 * @see https://umijs.org/docs/max/access#access
 * */
export default function access(
  initialState: { currentUser?: API.CurrentUser } | undefined
) {
  const { currentUser } = initialState ?? {};

  // 获取用户的菜单权限
  const userMenus = (currentUser as any)?.routers || [];

  // 使用迭代方式提取所有菜单的 menuKey，避免递归调用栈过深
  const extractMenuKeys = (menus: any[]): string[] => {
    if (!menus || menus.length === 0) return [];

    const keys: string[] = [];
    const stack = [...menus]; // 使用栈来模拟递归
    while (stack.length > 0) {
      const menu = stack.pop()!;
      if (menu.menuKey) {
        keys.push(menu.menuKey);
      }

      if (menu.children && menu.children.length > 0) {
        stack.push(...menu.children);
      }
    }

    return keys;
  };

  const userMenuKeys = extractMenuKeys(userMenus);

  // 使用 Set 提高查找效率，O(1) 时间复杂度
  const userMenuKeysSet = new Set(userMenuKeys);

  // 通用权限判断函数
  const hasPermission = (menuKey: string): boolean => {
    return userMenuKeysSet.has(menuKey);
  };

  /**
   * 将 menuKey 转换为权限标识
   * 转换规则：
   * - system -> canAccessSystem
   * - examList -> canAccessExamList
   * - stuExamList -> canAccessStuExamList
   * - add-exam -> canAccessAddExam
   */
  const convertMenuKeyToPermission = (menuKey: string): string => {
    // 处理短横线，转为驼峰
    const camelCase = menuKey.replace(/-([a-z])/g, (_, letter) =>
      letter.toUpperCase()
    );
    // 首字母大写
    const capitalized = camelCase.charAt(0).toUpperCase() + camelCase.slice(1);
    // 添加 canAccess 前缀
    return `canAccess${capitalized}`;
  };

  // 动态生成权限对象
  const permissions: Record<string, boolean> = {};

  // 为每个菜单 key 生成对应的权限判断
  userMenuKeys.forEach((menuKey) => {
    const permissionKey = convertMenuKeyToPermission(menuKey);
    permissions[permissionKey] = true;
  });

  // // 调试日志（开发环境可以打开）
  // if (process.env.NODE_ENV === 'development') {
  //   console.log('🔐 用户菜单权限:', {
  //     menuKeys: userMenuKeys,
  //     permissions: Object.keys(permissions),
  //   });
  // }

  // 返回动态生成的权限对象
  return {
    ...permissions,
    // 导出通用判断函数，供组件内部使用
    hasPermission,
    // 导出用户的所有菜单 keys，方便调试
    userMenuKeys: Array.from(userMenuKeysSet),
  };
}
